import { type FC, useEffect } from 'react'

import { type PimProductWithVariantsV1 } from '@redteclab/api/clients/bully'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import { useDeviceDetectorContext } from '../../../device-detector/context/deviceDetectorContext'
import { useOpenableControls } from '../../../generic-hooks/useOpenableControls/useOpenableControls'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { EXPERIMENT_TRACK_EVENT_OWN_BRANDS_OVERLAY_CLICK_ON_CLOSE } from '../../eventTracking/EXPERIMENTS_TRACK_EVENT'
import { experimentsTrackEvent } from '../../eventTracking/experimentsTrackEvent'
import { EXPERIMENT_OWN_BRANDS_OVERLAY } from '../../model/EXPERIMENT_NAME_PER_TENANT'
import { EXPERIMENT_VARIATION } from '../../model/EXPERIMENT_VARIATION'
import { experimentAccessorGetExperimentNamePerTenant } from '../../model/experimentAccessor'
import { useExperimentsContext } from '../ExperimentsContext'

import {
  EXPERIMENT_OWN_BRANDS_OVERLAY_SHOW_TIMEOUT,
  EXPERIMENT_OWN_BRANDS_OVERLAY_TITLE,
} from './ExperimentOwnBrandsOverlayConsts'
import { ExprimentOwnBrandsOverlayProductV1 } from './ExperimentOwnBrandsOverlayProductV1'
import { ExprimentOwnBrandsOverlayProductV2 } from './ExperimentOwnBrandsOverlayProductV2'

export const ExperimentOwnBrandsOverlay: FC<{
  experimentVariation?: EXPERIMENT_VARIATION
  ownBrandsProduct: PimProductWithVariantsV1 | null
}> = ({ experimentVariation, ownBrandsProduct }) => {
  const globalConfig = useGlobalConfigContext()
  const { locale, tenant } = globalConfig
  const { addExperimentKeysForActivation } = useExperimentsContext()
  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)
  const { isMobile } = useDeviceDetectorContext()

  const experimentOwnBrandsOverlayName =
    experimentAccessorGetExperimentNamePerTenant({
      experimentName: EXPERIMENT_OWN_BRANDS_OVERLAY,
      isMobile,
      tenant,
    })

  useEffect(() => {
    const showPopup = setTimeout(() => {
      if (
        experimentVariation &&
        [EXPERIMENT_VARIATION.V1, EXPERIMENT_VARIATION.V2].includes(
          experimentVariation,
        )
      ) {
        handleOpen()
      }

      if (experimentOwnBrandsOverlayName) {
        addExperimentKeysForActivation(experimentOwnBrandsOverlayName)
      }
    }, EXPERIMENT_OWN_BRANDS_OVERLAY_SHOW_TIMEOUT)

    return (): void => {
      clearTimeout(showPopup)
    }
  }, [
    addExperimentKeysForActivation,
    experimentOwnBrandsOverlayName,
    handleOpen,
    experimentVariation,
  ])

  return (
    (experimentVariation === EXPERIMENT_VARIATION.V1 ||
      experimentVariation === EXPERIMENT_VARIATION.V2) &&
    ownBrandsProduct && (
      <Dialog
        onClose={handleClose}
        open={isOpen}
        slotProps={{
          dialog: {
            className:
              'h-auto pb-3 max-w-[560px] w-10/12 tablet:w-full rounded-xl',
          },
        }}
      >
        <DialogTitle
          className="rounded-xl p-2 tablet:p-4"
          onClick={() => {
            experimentsTrackEvent(
              globalConfig,
              EXPERIMENT_TRACK_EVENT_OWN_BRANDS_OVERLAY_CLICK_ON_CLOSE,
            )
          }}
          onClose={handleClose}
        >
          <span className="text-m tablet:text-xl">
            {EXPERIMENT_OWN_BRANDS_OVERLAY_TITLE[locale]?.[experimentVariation]}
          </span>
        </DialogTitle>
        <DialogContent className="rounded-xl p-2 tablet:p-4">
          {experimentVariation === EXPERIMENT_VARIATION.V1 && (
            <ExprimentOwnBrandsOverlayProductV1
              ownBrandsProduct={ownBrandsProduct}
            />
          )}
          {experimentVariation === EXPERIMENT_VARIATION.V2 && (
            <ExprimentOwnBrandsOverlayProductV2
              ownBrandsProduct={ownBrandsProduct}
            />
          )}
        </DialogContent>
      </Dialog>
    )
  )
}
